/**
 * Greek catalog (Greek script) for the Phase 2 `MessageId` set — Agent 9, batch 1.
 *
 * `dir: 'ltr'`. Native-grade Modern Greek at a mathematician's register. **Warrant →
 * «Δικαιολόγηση»** (epistemic justification/warrant; Toulmin's «εγγύηση» is the flagged
 * alternative). Popper's refutation → διάψευση («Διαψευσμένο»); testable/tested →
 * Ελέγξιμο / Ελεγμένο. `scriptOf('el')` → greek, paired with the Greek Noto-led font stack.
 *
 * W/M grades stay Latin (`::numbering-system/latn`) — a grade is a stable id, not prose.
 * CLDR cardinal categories for Greek are one / other, so `frameActive` declares exactly those
 * (plus an explicit `=0`). Per INV-STABLE-ID a wrong translation is a presentation concern
 * only — every verdict stays fixed.
 */
import { P2 } from '../message-ids-phase2.js';
export const EL_CATALOG = {
    locale: 'el',
    dir: 'ltr',
    messages: {
        [P2.inquiryTitle]: 'Έρευνα',
        [P2.sectionClaims]: 'Ισχυρισμοί',
        [P2.sectionIntegrity]: 'Ακεραιότητα',
        [P2.axisWarrantLabel]: 'Δικαιολόγηση',
        [P2.axisWarrantValue]: 'W{w, number, ::numbering-system/latn}',
        [P2.axisEmpiricalLabel]: 'Εμπειρικό',
        [P2.axisEmpiricalValue]: '{kind, select, defn {Ορισμός} interp {Ερμηνεία} testable {Ελέγξιμο} tested {Ελεγμένο} refuted {Διαψευσμένο} program {Ερευνητικό πρόγραμμα} philos {Φιλοσοφικό} other {Άγνωστο}}',
        [P2.axisModalLabel]: 'Τροπικό',
        [P2.axisModalValue]: 'M{m, number, ::numbering-system/latn}',
        [P2.axisKidSummary]: 'Επίπεδο ισχύος {w, number, ::numbering-system/latn}',
        [P2.stateExplorable]: 'Διερευνήσιμο',
        [P2.stateRefuted]: 'Διαψευσμένο',
        [P2.stateOutside]: 'Εκτός πεδίου',
        [P2.verdictConflict]: 'Σε σύγκρουση με {constraints}',
        [P2.verdictOutside]: 'Απαιτεί {missing}',
        [P2.lossRegisterPlain]: 'Απλοποιήθηκε σε απλή γλώσσα — μέρος της ακρίβειας παραλείπεται.',
        [P2.lossRegisterKid]: 'Απλοποιήθηκε για νεαρούς αναγνώστες — πολλές λεπτομέρειες παραλείπονται.',
        [P2.lossProfileDetail]: 'Το πλήρες προφίλ Δικαιολόγηση / Εμπειρικό / Τροπικό συνοψίζεται εδώ.',
        [P2.lossConflictDetail]: 'Οι λεπτομέρειες της σύγκρουσης συντομεύονται σε αυτήν την προβολή.',
        [P2.laneVerified]: 'Επαληθευμένο',
        [P2.laneUnverified]: 'Μη επαληθευμένο',
        [P2.laneConflict]: 'Αποδεδειγμένη σύγκρουση',
        [P2.constraintLogic]: 'Λογική',
        [P2.constraintLightBends]: 'Το φως κάμπτεται σε βαρυτικό πεδίο',
        [P2.constraintParallelPostulate]: 'Το αίτημα των παραλλήλων',
        [P2.claimScalarGravity]: 'Βαθμωτή θεωρία της βαρύτητας',
        [P2.claimNonEuclidean]: 'Μη ευκλείδεια γεωμετρία',
        [P2.claimSubstrateSr]: 'Ερμηνεία υποστρώματος της ειδικής σχετικότητας',
        [P2.edgeExtObservation]: 'Εξωτερική παρατήρηση',
        [P2.edgeResourceSample]: 'Κοινός υπολογιστικός πόρος',
        [P2.edgeHeuristicSample]: 'Ευρετική καθοδήγηση',
        [P2.edgeSharedSample]: 'Κοινό θεμέλιο',
        // --- Phase 2 view-chrome ---
        [P2.sectionConstraints]: 'Περιορισμοί',
        [P2.sectionEdges]: 'Ακμές',
        [P2.sectionFrame]: 'Πλαίσιο',
        [P2.sectionExposure]: 'Έκθεση',
        [P2.statusAdoptedFormal]: 'Υιοθετήθηκε (τυπικά)',
        [P2.statusAdoptedInterpretive]: 'Υιοθετήθηκε (ερμηνευτικά)',
        [P2.statusSuspended]: 'Σε αναστολή',
        [P2.edgeEffectConstraint]: 'Συνθέτει έναν περιορισμό (μπορεί να διαψεύσει)',
        [P2.edgeEffectResource]: 'Πόρος — καμία επίδραση στις ετυμηγορίες',
        [P2.edgeEffectHeuristic]: 'Ευρετική — καμία επίδραση στις ετυμηγορίες',
        [P2.edgeEffectShared]: 'Κοινό θεμέλιο — καμία επίδραση στις ετυμηγορίες',
        [P2.frameActive]: '{count, plural, =0 {Κανένας ενεργός περιορισμός} one {# ενεργός περιορισμός} other {# ενεργοί περιορισμοί}}',
        [P2.exposureNone]: 'Καμία ερμηνευτική εξάρτηση',
        [P2.exposureDep]: 'Εξαρτάται από {id}',
        [P2.laneTrackMachine]: 'Μηχανή',
        [P2.laneTrackExpert]: 'Ειδικός',
        [P2.laneTrackCommunity]: 'Κοινότητα',
        [P2.expertAudited]: 'Ελεγμένο',
        [P2.expertInProgress]: 'Σε εξέλιξη',
        [P2.expertNotReviewed]: 'Μη εξετασμένο',
        [P2.communityFlags]: 'Υπάρχουν επισημάνσεις',
        [P2.communityNone]: 'Καμία επισήμανση',
        [P2.controlChallenge]: 'Αμφισβήτηση',
        [P2.controlActivate]: 'Ενεργοποίηση',
        [P2.controlMakeInterpretive]: 'Να γίνει ερμηνευτικό',
        [P2.controlImportEdge]: 'Εισαγωγή ακμής',
        [P2.controlPromote]: 'Προαγωγή',
        [P2.controlUndo]: 'Αναίρεση',
        [P2.controlRedo]: 'Επανάληψη',
        [P2.controlRegister]: 'Καταχώριση',
        [P2.promoteRejected]: 'Η προαγωγή απορρίφθηκε',
        [P2.promoteAccepted]: 'Η προαγωγή έγινε δεκτή',
        [P2.promoteReasonRefutationTerminal]: 'Μια απόδειξη δεν μπορεί να θεραπεύσει μια διάψευση.',
        [P2.promoteReasonKeystone]: 'Μια προαγωγή-ακρογωνιαίος λίθος χρειάζεται εμπειρική υποστήριξη.',
        [P2.promoteReasonEmpiricalNotFixed]: 'Το εμπειρικό είδος δεν είναι καθορισμένο.',
        [P2.promoteReasonModalNotFixed]: 'Ο τροπικός βαθμός δεν είναι καθορισμένος.',
        [P2.promoteReasonWarrantNotImproved]: 'Ο βαθμός δικαιολόγησης δεν βελτιώθηκε.',
        [P2.evidenceComputation]: "{engine} υπολόγισε {op} = {result} υπό {config}",
        [P2.lossNumericRounding]: "Το αποτέλεσμα στρογγυλοποιήθηκε σε {dp} δεκαδικά ψηφία",
    },
};
//# sourceMappingURL=el.js.map